import React, { Component } from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { get } from 'lodash';

import ClientsClass from '../../clients/ClientsClass';
import { ILoanAccount } from '../../interfaces';
import { Loader, Page } from '../common';

import { ILoanAccountFormData, withLeadSourceId } from './leadSource';
import LoanAccount from './LoanAccount';

const ERROR_MESSAGE = 'An error occurred loading the account.';

interface IProps {
  params: {
    id: string,
    main: string,
  };
}

interface IInjected extends IProps {
  Clients: ClientsClass;
}

@inject('Clients')
@autoBindMethods
@observer
class LoanAccountPage extends Component<IProps> {
  @observable private errorMessage: null | string = null;
  @observable private isLoading = false;
  @observable private loanAccount: null | ILoanAccount = null;
  // The account being fetched, which is what a new id has to be compared against. Comparing against
  // the loaded account would re-request the same id for as long as the first request is in flight.
  @observable private requestedId: null | string = null;

  public componentWillMount () {
    this.setStateFromProps(this.props);
  }

  // istanbul ignore next
  public componentWillReceiveProps (nextProps: IProps) {
    if (this.requestedId !== get(nextProps.params, 'id')) {
      this.setStateFromProps(nextProps);
    }
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  public async setStateFromProps (props: IProps) {
    const { params: { id } } = props
      , { Clients } = this.injected;

    this.errorMessage = null;
    this.isLoading = true;
    this.requestedId = id;

    try {
      const loanAccount = await Clients.loanAccounts.retrieve(id);

      // A different account was asked for while this one was in flight, so this response is stale
      if (this.requestedId !== id) { return; }

      this.loanAccount = loanAccount;
    }
    catch (err) {
      if (this.requestedId !== id) { return; }

      this.errorMessage = err.statusText || ERROR_MESSAGE;
    }
    finally {
      // Only the request still being waited on gets to take the loader down
      if (this.requestedId === id) { this.isLoading = false; }
    }
  }

  public async updateLoanAccount (data: ILoanAccountFormData) {
    const { params: { id } } = this.props
      , { Clients } = this.injected;

    // FormManager maps a 400 back onto the form fields, so let the error through rather than
    // swallowing it here. Transport has already toasted anything else.
    this.loanAccount = await Clients.loanAccounts.update(id, await withLeadSourceId(Clients, data));
  }

  public renderLoanAccount () {
    const { params: { main } } = this.props;

    if (this.isLoading) {
      return <Loader className='page-loader' logo />;
    }

    if (this.errorMessage) {
      return <div className='page-error'>{this.errorMessage}</div>;
    }

    if (!this.loanAccount) {
      // istanbul ignore next
      return <div className='page-error'>{ERROR_MESSAGE}</div>;
    }

    return (
      <LoanAccount
        key={this.loanAccount.id}
        loanAccount={this.loanAccount}
        main={main}
        onSave={this.updateLoanAccount}
      />
    );
  }

  public render () {
    return (
      <Page name='loan-account' type='detail'>
        <Page.Content>
          {this.renderLoanAccount()}
        </Page.Content>
      </Page>
    );
  }
}

export default LoanAccountPage;
